// ArcActionButton.tsx - Action button for arc transitions with multi-arc support

import React, { useRef, useState, useMemo } from 'react';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { MenuItem } from 'primereact/menuitem';
import { ArcTemplate, ArcInfo, AvailableAction } from 'types/arc';
import { useTranslation } from 'localization';
import './ArcActionButtonMolecule.scss';

export interface ArcWithTemplate {
  arc: ArcInfo;
  arcTemplate: ArcTemplate;
}

export interface ArcActionButtonProps {
  /** Single arc mode (backward compat) */
  arcTemplate?: ArcTemplate;
  currentStageId?: string;
  /** Multi-arc mode — array of arcs with their templates */
  arcs?: ArcWithTemplate[];
  onTransition: (targetStageId: string, comment?: string, arcId?: string) => Promise<void>;
  onCancel?: (arcId?: string) => Promise<void>;
  onRequestComment?: (targetStageId: string, arcId?: string) => void;
  disabled?: boolean;
}

const ArcActionButtonMolecule: React.FC<ArcActionButtonProps> = ({
  arcTemplate: singleArcTemplate,
  currentStageId: singleStageId,
  arcs,
  onTransition,
  onCancel,
  onRequestComment,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const menuRef = useRef<Menu>(null);
  const [loading, setLoading] = useState(false);

  // Normalize to multi-arc model
  const normalizedArcs: ArcWithTemplate[] = useMemo(() => {
    if (arcs && arcs.length > 0) return arcs;
    if (singleArcTemplate && singleStageId) {
      return [{
        arc: {
          id: '',
          templateSystemName: singleArcTemplate.system_name ?? '',
          templateVersion: 1,
          currentStageId: singleStageId,
          stageEnteredAt: '',
          isPrimary: true,
          stages: [],
        },
        arcTemplate: singleArcTemplate,
      }];
    }
    return [];
  }, [arcs, singleArcTemplate, singleStageId]);

  // Sort: primary first
  const sortedArcs = useMemo(() => {
    return [...normalizedArcs].sort((a, b) => {
      if (a.arc.isPrimary && !b.arc.isPrimary) return -1;
      if (!a.arc.isPrimary && b.arc.isPrimary) return 1;
      return 0;
    });
  }, [normalizedArcs]);

  // Defined before any early returns so the useMemo below remains
  // unconditional. The function is only invoked from menu callbacks, so
  // it's safe to allow `sortedArcs` to be empty here — early-returns
  // below prevent the user from ever reaching a state where the click
  // happens.
  const handleAction = async (targetStageId: string, arcId?: string) => {
    if (sortedArcs.length === 0) return;
    const entry = sortedArcs.find(e => e.arc.id === arcId) ?? sortedArcs[0];
    const stage = entry.arcTemplate.definition.stages.find(
      s => s.id === entry.arc.currentStageId
    );
    const requiresComment = stage?.transition?.requireComment ?? false;

    if (requiresComment) {
      onRequestComment?.(targetStageId, arcId);
    } else {
      setLoading(true);
      try {
        await onTransition(targetStageId, undefined, arcId);
      } finally {
        setLoading(false);
      }
    }
  };

  // Build menu items — grouped by arc for multi-arc.
  // Hook called unconditionally; loops over (possibly empty) sortedArcs.
  const menuItems: MenuItem[] = useMemo(() => {
    const items: MenuItem[] = [];
    const isMultiArc = sortedArcs.length > 1;

    for (const entry of sortedArcs) {
      const stage = entry.arcTemplate.definition.stages.find(
        s => s.id === entry.arc.currentStageId
      );
      if (!stage) continue;

      const actions = stage.transition?.availableActions || [];
      const allowCancel = entry.arcTemplate.definition.arcSettings?.allowManualCancellation ?? false;

      if (actions.length === 0 && !allowCancel) continue;

      // Add arc name header for multi-arc mode
      if (isMultiArc) {
        const arcName = entry.arcTemplate.name ?? entry.arc.templateSystemName;
        const label = entry.arc.isPrimary
          ? `${arcName} (${t('arc.atlas.primary')})`
          : arcName;

        if (items.length > 0) {
          items.push({ separator: true });
        }
        items.push({
          label,
          disabled: true,
          className: 'arc-action-group-header',
        });
      }

      for (const action of actions) {
        items.push({
          label: action.label,
          icon: 'pi pi-arrow-right',
          command: () => handleAction(action.nextStageId, entry.arc.id),
        });
      }

      if (allowCancel && onCancel) {
        if (items.length > 0 && items[items.length - 1].separator !== true) {
          items.push({ separator: true });
        }
        items.push({
          label: t('arc.actions.cancel_arc'),
          icon: 'pi pi-times-circle',
          className: 'arc-action-danger',
          command: async () => {
            setLoading(true);
            try {
              await onCancel(entry.arc.id);
            } finally {
              setLoading(false);
            }
          },
        });
      }
    }

    return items;
  }, [sortedArcs, onCancel, onTransition, onRequestComment, t]);

  // Early returns now run AFTER all hooks (Rules of Hooks)
  if (sortedArcs.length === 0) return null;

  const primaryEntry = sortedArcs[0];
  const primaryStage = primaryEntry.arcTemplate.definition.stages.find(
    s => s.id === primaryEntry.arc.currentStageId
  );

  if (!primaryStage) return null;

  const primaryActions: AvailableAction[] = primaryStage.transition?.availableActions || [];
  const primaryAllowCancel = primaryEntry.arcTemplate.definition.arcSettings?.allowManualCancellation ?? false;

  // Determine primary button label/action
  let primaryLabel = t('arc.actions.no_action');
  let primaryAction: (() => void | Promise<void>) | null = null;
  let primaryDisabled = true;

  if (primaryActions.length > 0) {
    const firstAction = primaryActions[0];
    primaryLabel = firstAction.label;
    primaryAction = () => handleAction(firstAction.nextStageId, primaryEntry.arc.id);
    primaryDisabled = false;
  } else if (primaryAllowCancel && onCancel) {
    primaryLabel = t('arc.actions.cancel_arc');
    primaryAction = async () => {
      setLoading(true);
      try {
        await onCancel(primaryEntry.arc.id);
      } finally {
        setLoading(false);
      }
    };
    primaryDisabled = false;
  }

  const hasDropdown = menuItems.length > 0;
  const showCommentIcon = !primaryStage.transition?.requireComment && primaryActions.length > 0;

  return (
    <div className="arc-action-button">
      <div className="arc-action-button-group">
        <Button
          type="button"
          label={primaryLabel}
          icon={loading ? 'pi pi-spin pi-spinner' : undefined}
          onClick={primaryAction || undefined}
          disabled={disabled || primaryDisabled || loading}
          className={`arc-action-primary ${hasDropdown ? 'has-dropdown' : ''}`}
          severity={primaryAllowCancel && menuItems.length === 0 ? 'danger' : 'success'}
        />

        {hasDropdown && (
          <Button
            type="button"
            icon="pi pi-chevron-down"
            onClick={(e) => menuRef.current?.toggle(e)}
            disabled={disabled || loading}
            className="arc-action-dropdown-trigger"
            severity="success"
            aria-label={t('arc.actions.more_actions')}
          />
        )}
      </div>

      {hasDropdown && (
        <Menu model={menuItems} popup ref={menuRef} className="arc-action-menu" />
      )}

      {showCommentIcon && onRequestComment && (
        <Button
          type="button"
          icon="pi pi-comment"
          onClick={() => {
            onRequestComment(primaryActions[0].nextStageId, primaryEntry.arc.id);
          }}
          disabled={disabled || loading}
          className="arc-action-comment"
          severity="secondary"
          outlined
          tooltip={t('arc.actions.add_comment')}
          tooltipOptions={{ position: 'top' }}
        />
      )}
    </div>
  );
};

export default ArcActionButtonMolecule;
